import React, { useContext, useEffect, useState } from "react";
import { makeStyles, createMuiTheme, ThemeProvider } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { IAgent } from "../interfaces";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import Collapse from "@material-ui/core/Collapse";
import Button from "@material-ui/core/Button";
import CallIcon from "@material-ui/icons/Call";
import Chip from "@material-ui/core/Chip";
import { blue, green } from "@material-ui/core/colors";
import Alert from "@material-ui/lab/Alert";
import { getLogger } from "../services/LoggingService";
import { ConnectDataContext } from "../contexts/DataContext";
import { CallContext } from "../contexts/CallContext";
import NotInterestedIcon from "@material-ui/icons/NotInterested";
import { TextField } from "@material-ui/core";
import { sortBy } from "lodash";
import AppContext from "../contexts/AppContextProvider";
import { Logger } from "../services/LoggingService";

const logger = getLogger("AgentTransfer");
const theme = createMuiTheme({
    palette: {
        primary: {
            main: blue[500],
        },
        secondary: {
            main: green[500],
        },
    },
});

interface IProps {
    agents: IAgent[];
}
const useStyles = makeStyles({
    table: {
        maxWidth: 330,
    },
    alert: {
        marginTop: "10px",
    },
    agentSearchInput: {
        marginTop: "10px",
        width: "100%",
    },
});

const useRowStyles = makeStyles({
    root: {
        "& > *": {
            borderBottom: "unset",
            padding: "0",
        },
    },
    mb10: {
        marginBottom: "10px",
    },
});

function Row(props: { agent: IAgent; quickConnectEP: connect.Endpoint }) {
    const { agent, quickConnectEP } = props;
    const [open, setOpen] = React.useState(false);
    const callContext = useContext(CallContext);
    const {
        config: { agentTransferConfig },
    } = useContext(AppContext);
    const classes = useRowStyles();
    const onCallFailure = (err: any): void => {
        err = JSON.parse(err);
        logger.debug(err);
    };
    const onCallSuccess = (): void => {
        logger.debug("SUCCESSFUL TRANSFER");
    };

    const transferToAgentEndpoint = async (qcEp: connect.Endpoint) => {
        if (!callContext.agent) return;
        const agentContacts = callContext.agent.getContacts(connect.ContactType.VOICE);
        if (agentContacts.length === 0) {
            return;
        } else if (agentContacts.length === 1) {
            agentContacts[0].addConnection(qcEp, {
                success: onCallSuccess,
                failure: onCallFailure,
            });
        } else {
            throw new Error("Already have 2 active voice contacts");
        }
    };
    return (
        <React.Fragment>
            <TableRow className={classes.root}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {agent.FirstName} {agent.LastName}
                </TableCell>
                <TableCell align="right">
                    {quickConnectEP && agent.Status === "Available" ? (
                        <Chip
                            size="small"
                            color="secondary"
                            onClick={() => transferToAgentEndpoint(quickConnectEP)}
                            icon={quickConnectEP ? <CallIcon /> : <NotInterestedIcon />}
                            label={agent.Status.charAt(0).toUpperCase() + agent.Status.slice(1).toLowerCase()}
                        />
                    ) : (
                        <Chip
                            size="small"
                            label={agent.Status.charAt(0).toUpperCase() + agent.Status.slice(1).toLowerCase()}
                            disabled
                        />
                    )}
                </TableCell>
            </TableRow>
            <TableRow className={classes.root}>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box margin={1}>
                            <Button
                                className={classes.mb10}
                                disabled={!quickConnectEP || agent.Status !== "Available"}
                                onClick={() => transferToAgentEndpoint(quickConnectEP)}
                                size="small"
                                variant="contained"
                                color="primary"
                            >
                                {!quickConnectEP || agent.Status !== "Available" ? "Cannot transfer" : "Transfer Call"}
                            </Button>
                            <Table size="small" aria-label="purchases">
                                <TableBody>
                                    <TableRow className={classes.root}>
                                        <TableCell>Quick Connect Linked:</TableCell>
                                        <TableCell>{quickConnectEP ? "YES" : "NOT LINKED"}</TableCell>
                                    </TableRow>
                                    <TableRow className={classes.root}>
                                        <TableCell>Active Queue:</TableCell>
                                        <TableCell>
                                            {["CONNECTED", "CONNECTING"].includes(agent.Status.toUpperCase())
                                                ? agent.Queue
                                                : "NA"}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow className={classes.root}>
                                        <TableCell>Team:</TableCell>
                                        <TableCell>{agent.HierarchyLevel2 ? agent.HierarchyLevel2 : "NA"}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}
interface IAvailableAgentEPDict {
    [name: string]: connect.Endpoint;
}
const AgentTransfer = ({ agents }: IProps): JSX.Element => {
    const classes = useStyles();
    const [availableAgentEPDict, setAvailableAgentEPDict] = useState<IAvailableAgentEPDict>({});
    const { availableAgentEndpoints } = useContext(ConnectDataContext);
    const [agentSearch, setAgentSearch] = useState("");
    const [filteredAgents, setFilteredAgents] = useState<IAgent[]>([]);
    useEffect(() => {
        if (availableAgentEndpoints.length === 0) {
            setAvailableAgentEPDict({});
        } else {
            const newDict = availableAgentEndpoints.reduce((dict: IAvailableAgentEPDict, ep) => {
                const key: string = ep.name;
                dict[key] = ep;
                return dict;
            }, {});
            setAvailableAgentEPDict(newDict);
        }
    }, [availableAgentEndpoints]);

    useEffect(() => {
        if (!agents) {
            setFilteredAgents([]);
        } else {
            if (!agentSearch) {
                setFilteredAgents(sortBy(agents, ["LastName", "FirstName"]));
            } else {
                const filtered = agents.filter((agent) => {
                    return (
                        agent.AgentUsername.toLowerCase().includes(agentSearch.toLowerCase()) ||
                        agent.LastName.toLowerCase().includes(agentSearch.toLowerCase()) ||
                        agent.FirstName.toLowerCase().includes(agentSearch.toLowerCase())
                    );
                });
                setFilteredAgents(sortBy(filtered, ["LastName", "FirstName"]));
            }
        }
    }, [agentSearch, agents]);

    // const availableQuickConnectForAgent = (userName: string) =>
    //     Object.prototype.hasOwnProperty.call(availableAgentEPDict, userName);
    const getQuickConnectEndpoint = (userName: string) => availableAgentEPDict[userName];
    function renderRows(agents: IAgent[]): JSX.Element[] {
        return agents.map((agent: IAgent, i: number) => {
            return (
                <Row
                    key={agent.AgentUsername + i}
                    agent={agent}
                    // Tim Burton 06/22/21
                    // We need to concat the "Display Name" because that is what is being used for the quick connect names
                    quickConnectEP={getQuickConnectEndpoint(
                        `[${agent.HierarchyLevel1}] ${agent.FirstName} ${agent.LastName}`
                    )}
                />
            );
        });
    }

    return (
        <ThemeProvider theme={theme}>
            {agents.length === 0 ? (
                <Alert className={classes.alert} severity={"info"}>
                    No Available Agents
                </Alert>
            ) : (
                <TableContainer>
                    <TextField
                        onChange={(e) => setAgentSearch(e.target.value)}
                        className={classes.agentSearchInput}
                        size="small"
                        label="Search ..."
                        variant="outlined"
                    />
                    {filteredAgents.length === 0 ? (
                        <Alert className={classes.alert} severity={"info"}>
                            No search results
                        </Alert>
                    ) : (
                        <Table className={classes.table} size="small" aria-label="available agent table">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell>Name</TableCell>
                                    <TableCell align="right">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>{renderRows(filteredAgents)}</TableBody>
                        </Table>
                    )}
                </TableContainer>
            )}
        </ThemeProvider>
    );
};

export default AgentTransfer;
